import { UserButton, useUser } from '@clerk/clerk-react'
import { cn } from '@/lib/utils'

/**
 * Shared appearance for every Clerk widget we render (sidebar, mobile, top
 * bar, onboarding). Defined once so the popover chrome is always in sync
 * with the dashboard theme.
 *
 * The card is deliberately translucent + backdrop-blurred so the fixed
 * MatrixRain canvas behind the app shows through — Clerk renders the menu
 * in a portal outside our React tree, so we can't mount a child canvas in
 * it. Leaking the rain through translucency is the closest analog and keeps
 * the popup feeling continuous with the rest of the page.
 */
export const CLERK_APPEARANCE = {
  variables: {
    colorPrimary: '#1C3FFF',
    colorBackground: 'rgba(19, 20, 28, 0.85)',
    colorText: '#F1F5F9',
    colorTextSecondary: '#9CA3AF',
    colorTextOnPrimaryBackground: '#FFFFFF',
    colorNeutral: '#F1F5F9',
    colorInputBackground: 'rgba(255,255,255,0.03)',
    colorInputText: '#F1F5F9',
    colorDanger: '#F87171',
    colorSuccess: '#10B981',
    colorWarning: '#FBBF24',
    borderRadius: '0.5rem',
    fontFamily: 'Inter, system-ui, sans-serif',
  },
  elements: {
    // Outer container Clerk mounts everything inside. Force a light default
    // text color so any Clerk subcomponent we haven't explicitly themed
    // inherits a legible color instead of falling back to near-black on
    // our dark card.
    rootBox: 'text-[var(--color-silver-bright)]',
    avatarBox: 'h-8 w-8 ring-1 ring-[rgba(28,63,255,0.3)]',
    // Popover card — the main menu container. Translucent + blur lets the
    // matrix-rain bleed through for a continuous theme.
    userButtonPopoverCard:
      'bg-[rgba(19,20,28,0.85)] backdrop-blur-xl border border-[rgba(28,63,255,0.18)] shadow-[0_0_60px_rgba(28,63,255,0.15)] text-[var(--color-silver-bright)]',
    userButtonPopoverMain: 'bg-transparent',
    userButtonPopoverActions: 'bg-transparent border-t border-[rgba(255,255,255,0.06)]',
    userButtonPopoverActionButton:
      'text-[var(--color-silver-bright)] hover:bg-[rgba(28,63,255,0.1)] hover:text-[var(--color-silver-bright)]',
    userButtonPopoverActionButtonIcon: 'text-[var(--color-cobalt-bright)]',
    userButtonPopoverActionButtonText: 'text-[var(--color-silver-bright)]',
    userButtonPopoverFooter: 'bg-transparent border-t border-[rgba(255,255,255,0.06)]',
    userPreviewMainIdentifier: 'text-[var(--color-silver-bright)]',
    userPreviewSecondaryIdentifier: 'text-[var(--color-silver)]',
    userButtonTrigger: 'focus:shadow-[0_0_0_3px_rgba(28,63,255,0.25)]',
    // Styles for the full account management Dialog (appears after "Manage
    // account" in the popover) *and* the SignIn/SignUp widget cards on
    // /login and /signup. Default text color on the card is critical —
    // without it, Clerk subcomponents we haven't explicitly themed render
    // as near-black on this dark card and become invisible. Primary
    // content is pushed to pure white (the gray-300 fallback read as
    // washed-out on the cobalt card); subtitles keep a silver tone for
    // hierarchy.
    card:
      'bg-[rgba(19,20,28,0.92)] backdrop-blur-xl border border-[rgba(28,63,255,0.18)] shadow-[0_0_60px_rgba(28,63,255,0.15)] text-white',
    cardBox: 'text-white',
    header: 'text-white',
    headerTitle: 'text-white',
    headerSubtitle: 'text-[var(--color-silver)]',
    main: 'text-white',
    // "Logo" block at the top of SignIn/SignUp widgets — Clerk may render
    // the app name as plain text here; keep it readable.
    logoBox: 'text-white',
    navbar: 'bg-[rgba(255,255,255,0.02)] border-r border-[rgba(255,255,255,0.06)]',
    navbarButton:
      'text-[var(--color-silver)] hover:bg-[rgba(28,63,255,0.1)] hover:text-[var(--color-silver-bright)]',
    navbarButton__active:
      'bg-[rgba(28,63,255,0.12)] text-[var(--color-silver-bright)]',
    profileSectionTitleText: 'text-[var(--color-silver-bright)]',
    profileSectionContent: 'text-[var(--color-silver)]',
    formButtonPrimary:
      'bg-[var(--color-cobalt)] hover:bg-[var(--color-cobalt-bright)] shadow-none hover:shadow-[0_0_24px_rgba(28,63,255,0.45)] text-white',
    formFieldLabel: 'text-white',
    formFieldLabelRow: 'text-white',
    formFieldInput:
      'bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.08)] text-white placeholder:text-[var(--color-silver)] focus:border-[rgba(28,63,255,0.4)]',
    formFieldInputShowPasswordButton: 'text-[var(--color-silver)] hover:text-[var(--color-silver-bright)]',
    formFieldHintText: 'text-[var(--color-silver)]',
    formFieldErrorText: 'text-red-300',
    formFieldSuccessText: 'text-emerald-300',
    formFieldAction: 'text-[var(--color-cobalt-bright)] hover:underline',
    formFieldRow: 'text-[var(--color-silver-bright)]',
    formResendCodeLink: 'text-[var(--color-cobalt-bright)] hover:underline',
    // Social-login buttons (Google / GitHub / etc). Default Clerk text color
    // resolves to near-black on dark backgrounds and is unreadable. Force
    // explicit white text on the button + the icon so they stay legible.
    socialButtonsBlockButton:
      'border border-[rgba(255,255,255,0.12)] bg-[rgba(255,255,255,0.04)] text-white hover:border-[rgba(28,63,255,0.4)] hover:bg-[rgba(28,63,255,0.08)] hover:text-white',
    socialButtonsBlockButtonText: 'text-white font-medium',
    socialButtonsBlockButtonArrow: 'text-[var(--color-silver)]',
    socialButtonsProviderIcon: 'opacity-100',
    // GitHub provider hidden from login (per product call — only Google +
    // email sign-in surface for now). Targets both the full-width block
    // variant and the compact icon-only variant so neither layout shows
    // a GitHub button. Re-enable by removing these two `hidden` rules
    // and reinstating the brightness/invert filter that used to live here
    // (Octocat is a black-on-transparent SVG and needs whitening).
    //
    // NOTE: Google is intentionally NOT hidden here — it stays on the LOGIN
    // page (<SignIn>). It is hidden ONLY on the SIGNUP page via a per-component
    // appearance override in pages/signup.tsx, because Google-first signups
    // land in an account that can't be signed back into (Clerk linking quirk).
    socialButtonsBlockButton__github: 'hidden',
    socialButtonsIconButton:
      'border border-[rgba(255,255,255,0.12)] bg-[rgba(255,255,255,0.04)] hover:border-[rgba(28,63,255,0.4)] hover:bg-[rgba(28,63,255,0.08)]',
    socialButtonsIconButton__github: 'hidden',
    // Divider between social and email auth ("or").
    dividerLine: 'bg-[rgba(255,255,255,0.08)]',
    dividerText: 'text-[var(--color-silver)]',
    // Identity preview row (shown on second auth step, e.g. password entry).
    identityPreview:
      'bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.08)]',
    identityPreviewText: 'text-white',
    identityPreviewEditButton: 'text-[var(--color-cobalt-bright)] hover:text-[var(--color-cobalt-bright)] hover:underline',
    // Bottom links: "Sign up" / "Sign in" / "Forgot password".
    footer: 'bg-transparent',
    footerAction: 'text-[var(--color-silver)]',
    footerActionText: 'text-[var(--color-silver)]',
    footerActionLink: 'text-[var(--color-cobalt-bright)] hover:underline font-medium',
    // Alerts / notices inside Clerk forms.
    alert: 'bg-red-500/10 border border-red-500/25 text-red-300',
    alertText: 'text-red-300',
    alertIcon: 'text-red-300',
    // OTP / verification code inputs.
    otpCodeFieldInput:
      'bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.08)] text-white',
    // "Sign in to..." / "Welcome back" titles inside login/signup widgets.
    formHeaderTitle: 'text-white',
    formHeaderSubtitle: 'text-[var(--color-silver)]',
  },
} as const

type UserMenuProps = {
  readonly collapsed?: boolean
  readonly className?: string
  /** Override the afterSignOutUrl. Defaults to /login. */
  readonly afterSignOutUrl?: string
}

/**
 * Sidebar account block — Clerk avatar + (when expanded) the user's name
 * and email in a compact two-line strip. Clicking anywhere on the row
 * opens Clerk's popover; the avatar itself is the actual trigger, the
 * surrounding wrapper just adds hover lift + the name text.
 */
export function UserMenu({ collapsed = false, className, afterSignOutUrl = '/login' }: UserMenuProps) {
  const { user } = useUser()
  const primaryName =
    user?.fullName?.trim() ||
    [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() ||
    user?.username ||
    user?.primaryEmailAddress?.emailAddress?.split('@')[0] ||
    'Account'
  const email = user?.primaryEmailAddress?.emailAddress ?? ''

  return (
    <div
      className={cn(
        'group flex items-center gap-3 rounded-md px-2 py-1.5 transition-colors',
        !collapsed && 'hover:bg-[rgba(255,255,255,0.04)]',
        collapsed && 'justify-center',
        className,
      )}
    >
      <UserButton
        afterSignOutUrl={afterSignOutUrl}
        appearance={CLERK_APPEARANCE}
      />
      {!collapsed && (
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-[var(--color-silver-bright)]">
            {primaryName}
          </p>
          {email && (
            <p className="truncate text-[11px] text-[var(--color-silver)]">{email}</p>
          )}
        </div>
      )}
    </div>
  )
}
